import { useState, useEffect } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { api } from '../../utils/api';
import {
  HardDrive, Users, Package, Contact, Zap, Crown,
  Gauge, Edit3, Camera, X, ChevronRight,
} from 'lucide-react';

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface PlanTier {
  name: string;
  maxUsers: number;
  maxStorageMB: number;
  maxApiCallsPerMonth: number;
  maxContacts: number;
  maxProducts: number;
}

interface TenantResource {
  tenant: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    createdAt: string;
  };
  quota: {
    plan: string;
    maxUsers: number;
    maxStorageMB: number;
    maxApiCallsPerMonth: number;
    maxContacts: number;
    maxProducts: number;
  } | null;
  usage: {
    userCount: number;
    contactCount: number;
    productCount: number;
    invoiceCount: number;
    dealCount: number;
    ticketCount: number;
    storageMB: number;
  };
  percentages: {
    users: number;
    storage: number;
    apiCalls: number;
    contacts: number;
    products: number;
  };
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

function formatLimit(value: number, suffix = ''): string {
  if (value === 0) return 'Unlimited';
  if (value >= 1024 && suffix === 'MB') return `${(value / 1024).toFixed(0)} GB`;
  return `${value.toLocaleString('en-IN')}${suffix ? ` ${suffix}` : ''}`;
}

function usageBarColor(pct: number): string {
  if (pct > 95) return 'bg-red-500';
  if (pct > 80) return 'bg-orange-500';
  if (pct > 60) return 'bg-yellow-500';
  return 'bg-emerald-500';
}

function usageTextColor(pct: number): string {
  if (pct > 95) return 'text-red-600';
  if (pct > 80) return 'text-orange-600';
  if (pct > 60) return 'text-yellow-600';
  return 'text-gray-600';
}

function UsageBar({ used, max, label }: { used: number; max: number; label?: string }) {
  const isUnlimited = max === 0;
  const pct = isUnlimited ? 0 : Math.min((used / max) * 100, 100);
  const displayPct = isUnlimited ? 0 : Math.round(pct * 10) / 10;

  return (
    <div className="space-y-1">
      {label && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">{label}</span>
          <span className={`font-medium ${usageTextColor(displayPct)}`}>
            {isUnlimited ? `${used}` : `${used} / ${max}`}
            {isUnlimited && <span className="ml-1 text-gray-400">(unlimited)</span>}
          </span>
        </div>
      )}
      <div className="h-2 w-full rounded-full bg-gray-100">
        <div
          className={`h-2 rounded-full transition-all ${isUnlimited ? 'bg-gray-300' : usageBarColor(displayPct)}`}
          style={{ width: isUnlimited ? '5%' : `${Math.max(pct, 1)}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Plan Tier Cards
// ---------------------------------------------------------------------------

const PLAN_COLORS: Record<string, { bg: string; border: string; icon: string }> = {
  FREE: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'text-gray-500' },
  STARTER: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  GROWTH: { bg: 'bg-violet-50', border: 'border-violet-200', icon: 'text-violet-600' },
  ENTERPRISE: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600' },
};

function PlanCard({ planKey, tier }: { planKey: string; tier: PlanTier }) {
  const colors = PLAN_COLORS[planKey] || PLAN_COLORS.FREE;
  return (
    <div className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <Crown size={16} className={colors.icon} />
        <h3 className="text-sm font-bold text-gray-900">{tier.name}</h3>
      </div>
      <div className="space-y-1.5 text-xs text-gray-600">
        <div className="flex justify-between">
          <span>Users</span>
          <span className="font-medium text-gray-900">{formatLimit(tier.maxUsers)}</span>
        </div>
        <div className="flex justify-between">
          <span>Storage</span>
          <span className="font-medium text-gray-900">{formatLimit(tier.maxStorageMB, 'MB')}</span>
        </div>
        <div className="flex justify-between">
          <span>API Calls/mo</span>
          <span className="font-medium text-gray-900">{formatLimit(tier.maxApiCallsPerMonth)}</span>
        </div>
        <div className="flex justify-between">
          <span>Contacts</span>
          <span className="font-medium text-gray-900">{formatLimit(tier.maxContacts)}</span>
        </div>
        <div className="flex justify-between">
          <span>Products</span>
          <span className="font-medium text-gray-900">{formatLimit(tier.maxProducts)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Edit Quota Modal
// ---------------------------------------------------------------------------

function EditQuotaModal({
  tenant,
  quota,
  onClose,
  onSave,
}: {
  tenant: TenantResource['tenant'];
  quota: TenantResource['quota'];
  onClose: () => void;
  onSave: () => void;
}) {
  const [plan, setPlan] = useState(quota?.plan || 'STARTER');
  const [maxUsers, setMaxUsers] = useState(quota?.maxUsers ?? 15);
  const [maxStorageMB, setMaxStorageMB] = useState(quota?.maxStorageMB ?? 2048);
  const [maxApiCalls, setMaxApiCalls] = useState(quota?.maxApiCallsPerMonth ?? 50000);
  const [maxContacts, setMaxContacts] = useState(quota?.maxContacts ?? 5000);
  const [maxProducts, setMaxProducts] = useState(quota?.maxProducts ?? 500);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/admin/resources/${tenant.id}/quota`, {
        plan,
        maxUsers,
        maxStorageMB,
        maxApiCallsPerMonth: maxApiCalls,
        maxContacts,
        maxProducts,
      });
      onSave();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update quota');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} className="!bg-white !shadow-2xl">
      <div>
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200 sticky top-0 z-10 bg-white rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-900">
            Edit Quota &mdash; {tenant.name}
          </h2>
          <RequestCloseButton className="rounded-lg p-1 hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </RequestCloseButton>
        </div>
        <div className="p-6 pt-4">

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="space-y-4">
          {/* Plan selector */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
            >
              <option value="FREE">Free</option>
              <option value="STARTER">Starter</option>
              <option value="GROWTH">Growth</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Users</label>
              <input
                type="number"
                min={0}
                value={maxUsers}
                onChange={(e) => setMaxUsers(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
              <p className="mt-0.5 text-[10px] text-gray-400">0 = unlimited</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Storage (MB)</label>
              <input
                type="number"
                min={0}
                value={maxStorageMB}
                onChange={(e) => setMaxStorageMB(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">API Calls/mo</label>
              <input
                type="number"
                min={0}
                value={maxApiCalls}
                onChange={(e) => setMaxApiCalls(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
              <p className="mt-0.5 text-[10px] text-gray-400">0 = unlimited</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Contacts</label>
              <input
                type="number"
                min={0}
                value={maxContacts}
                onChange={(e) => setMaxContacts(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
              <p className="mt-0.5 text-[10px] text-gray-400">0 = unlimited</p>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Products</label>
              <input
                type="number"
                min={0}
                value={maxProducts}
                onChange={(e) => setMaxProducts(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
              />
              <p className="mt-0.5 text-[10px] text-gray-400">0 = unlimited</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <RequestCloseButton
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </RequestCloseButton>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Quota'}
          </button>
        </div>
        </div>
      </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}

// ---------------------------------------------------------------------------
//  Main Component
// ---------------------------------------------------------------------------

export function ResourceManagement() {
  const [tenants, setTenants] = useState<TenantResource[]>([]);
  const [plans, setPlans] = useState<Record<string, PlanTier>>({});
  const [loading, setLoading] = useState(true);
  const [editingTenant, setEditingTenant] = useState<TenantResource | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const [tenantsRes, plansRes] = await Promise.all([
        api.get('/admin/resources'),
        api.get('/admin/resources/plans'),
      ]);
      setTenants(tenantsRes.data.data);
      setPlans(plansRes.data.data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSnapshot = async (tenantId: string) => {
    setSnapshotLoading(tenantId);
    try {
      await api.post(`/admin/resources/${tenantId}/snapshot`);
      // Refresh data
      await fetchData();
    } catch {
      // handled
    } finally {
      setSnapshotLoading(null);
    }
  };

  const handlePlanChange = async (tenantId: string, newPlan: string) => {
    try {
      await api.put(`/admin/resources/${tenantId}/quota`, { plan: newPlan });
      await fetchData();
    } catch {
      // handled
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Resource Management</h1>
        <p className="text-sm text-gray-500">Monitor and manage tenant resource allocation</p>
      </div>

      {/* Plan Tier Cards */}
      {Object.keys(plans).length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(plans).map(([key, tier]) => (
            <PlanCard key={key} planKey={key} tier={tier} />
          ))}
        </div>
      )}

      {/* Tenant Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h2 className="text-sm font-semibold text-gray-900">
            Tenant Allocation ({tenants.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 font-medium text-gray-500">Tenant</th>
                <th className="px-4 py-3 font-medium text-gray-500">Plan</th>
                <th className="px-4 py-3 font-medium text-gray-500 min-w-[140px]">Users</th>
                <th className="px-4 py-3 font-medium text-gray-500 min-w-[140px]">Storage</th>
                <th className="px-4 py-3 font-medium text-gray-500 min-w-[120px]">Contacts</th>
                <th className="px-4 py-3 font-medium text-gray-500 min-w-[120px]">Products</th>
                <th className="px-4 py-3 font-medium text-gray-500 min-w-[120px]">API Calls</th>
                <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tenants.map((t) => {
                const q = t.quota;
                const u = t.usage;
                const maxUsers = q?.maxUsers ?? 15;
                const maxStorage = q?.maxStorageMB ?? 2048;
                const maxContacts = q?.maxContacts ?? 5000;
                const maxProducts = q?.maxProducts ?? 500;
                const maxApi = q?.maxApiCallsPerMonth ?? 50000;

                return (
                  <tr key={t.tenant.id} className="hover:bg-gray-50/50">
                    {/* Tenant Name */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{t.tenant.name}</p>
                        <p className="text-[10px] text-gray-400">{t.tenant.slug}</p>
                      </div>
                    </td>

                    {/* Plan Dropdown */}
                    <td className="px-4 py-3">
                      <select
                        value={q?.plan || 'STARTER'}
                        onChange={(e) => handlePlanChange(t.tenant.id, e.target.value)}
                        className="rounded border border-gray-200 px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
                      >
                        <option value="FREE">Free</option>
                        <option value="STARTER">Starter</option>
                        <option value="GROWTH">Growth</option>
                        <option value="ENTERPRISE">Enterprise</option>
                      </select>
                    </td>

                    {/* Users */}
                    <td className="px-4 py-3">
                      <UsageBar used={u.userCount} max={maxUsers} />
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        {u.userCount}/{formatLimit(maxUsers)}
                      </p>
                    </td>

                    {/* Storage */}
                    <td className="px-4 py-3">
                      <UsageBar used={u.storageMB} max={maxStorage} />
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        {u.storageMB.toFixed(1)} MB / {formatLimit(maxStorage, 'MB')}
                      </p>
                    </td>

                    {/* Contacts */}
                    <td className="px-4 py-3">
                      <UsageBar used={u.contactCount} max={maxContacts} />
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        {u.contactCount}/{formatLimit(maxContacts)}
                      </p>
                    </td>

                    {/* Products */}
                    <td className="px-4 py-3">
                      <UsageBar used={u.productCount} max={maxProducts} />
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        {u.productCount}/{formatLimit(maxProducts)}
                      </p>
                    </td>

                    {/* API Calls */}
                    <td className="px-4 py-3">
                      <UsageBar used={0} max={maxApi} />
                      <p className="mt-0.5 text-[10px] text-gray-400">
                        0/{formatLimit(maxApi)}
                      </p>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingTenant(t)}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit Quota"
                        >
                          <Edit3 size={14} />
                        </button>
                        <button
                          onClick={() => handleSnapshot(t.tenant.id)}
                          disabled={snapshotLoading === t.tenant.id}
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                          title="Take Snapshot"
                        >
                          <Camera size={14} className={snapshotLoading === t.tenant.id ? 'animate-pulse' : ''} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {tenants.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                    No active tenants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Quota Modal */}
      {editingTenant && (
        <EditQuotaModal
          tenant={editingTenant.tenant}
          quota={editingTenant.quota}
          onClose={() => setEditingTenant(null)}
          onSave={() => {
            setEditingTenant(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
